import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import {LightV1, fromRenderStructDTOArray, SceneEnvironment} from '@3dcalc/3dpClient';
import { Board } from '@3dcalc/3dpClient';
import * as THREE from 'three';

// ─── Конфиг ───────────────────────────────────────────────────────────────
const MODEL_URL = '/model/787d8945-c592-4c1d-9727-7d6a8c8df603.json';

interface AssemblyConfig {
    hideDistance?: number;   // база подъёма по Y (0 = авто: 2× макс Y)
    liftFactor?: number;     // множитель подъёма: 1 = высоко, 0.4 = низкий подъём → боковой прилёт заметнее
    spreadFactor?: number;   // веер по X/Z пропорц. |target|: дальние разлетаются сильнее
    spreadBase?: number;     // аддитивный боковой сдвиг для ВСЕХ (в т.ч. центральных, у кого target=0)
    itemDuration?: number;   // мс — влёт одного элемента
    stagger?: number;        // мс — задержка старта между соседними элементами
    holdMs?: number;         // мс — пауза между сборкой и разбором (и после разбора перед новым циклом)
    groupOrder?: number[];   // ручной порядок групп по groupIndex; ненайденные — в конец
    autoStart?: boolean;
    loop?: boolean;
}

const DEFAULTS: Required<AssemblyConfig> = {
    hideDistance: 0,         // 0 → авто
    liftFactor: 1.1,         // подъём выше кадра
    spreadFactor: 2.2,       // сильный веер
    spreadBase: 7,           // база бокового сдвига — уводит старт за край вида
    itemDuration: 700,
    stagger: 120,
    holdMs: 1200,
    groupOrder: [0, 9, 6, 2, 5, 4, 7, 3],
    autoStart: true,
    loop: true,
};

// ─── Плоский элемент ────────────────────────────────────────────────────────
type FlatItem = {
    key: string;
    vertices: number[];
    indices: number[];
    uvs: number[];
    material: any;
    groupPos: [number, number, number];
    groupRot: [number, number, number];
    targetPos: [number, number, number];
    targetRot: [number, number, number];
    startPos: [number, number, number];
    groupIndex: number;
};

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

// position/rotation могут прийти как {x,y,z} или как [x,y,z] — читаем оба
const vec3 = (v: any): [number, number, number] => {
    if (!v) return [0, 0, 0];
    if (Array.isArray(v)) return [v[0] ?? 0, v[1] ?? 0, v[2] ?? 0];
    return [v.x ?? 0, v.y ?? 0, v.z ?? 0];
};

// struct → плоский упорядоченный список элементов
const buildItems = (
    struct: any[],
    cfg: Required<AssemblyConfig>,
): { items: FlatItem[]; autoHide: number } => {
    // максимальная Y для авто-высоты подъёма
    let maxY = 0;
    struct.forEach((g) => {
        const gy = vec3(g.position)[1];
        g.elements.forEach((el: any) => {
            const y = gy + vec3(el.position)[1];
            if (y > maxY) maxY = y;
        });
    });
    const autoHide = maxY * 2 || 20;
    const baseLift = cfg.hideDistance > 0 ? cfg.hideDistance : autoHide;
    const lift = baseLift * cfg.liftFactor;

    // боковое смещение по одной оси: наружу от центра.
    // = знак*(|t|*spreadFactor + spreadBase); при t=0 берём +base, чтобы центр тоже уходил вбок
    const offset = (t: number) => {
        const s = t === 0 ? 1 : Math.sign(t);
        return s * (Math.abs(t) * cfg.spreadFactor + cfg.spreadBase);
    };

    const items: FlatItem[] = [];
    struct.forEach((g, gi) => {
        const groupPos = vec3(g.position);
        const groupRot = vec3(g.rotation);
        g.elements.forEach((el: any, ei: number) => {
            const targetPos = vec3(el.position);
            const targetRot = vec3(el.rotation);
            const startPos: [number, number, number] = [
                targetPos[0] + offset(targetPos[0]),
                targetPos[1] + lift,
                targetPos[2] + offset(targetPos[2]),
            ];
            items.push({
                key: `${g.group}-${gi}-${ei}`,
                vertices: g.vertices[el.vertices],
                indices: g.indices[el.indices],
                uvs: g.uvs[el.uvs],
                material: g.material[el.material],
                groupPos,
                groupRot,
                targetPos,
                targetRot,
                startPos,
                groupIndex: gi,
            });
        });
    });
    return { items, autoHide };
};

// ─── Элемент: тупой исполнитель влёта ────────────────────────────────────────
// progress: 0 = в стартовой (разлетелся/спрятан), 1 = на месте (собран)
const FlyBoard: React.FC<{ item: FlatItem; progressRef: React.MutableRefObject<number> }> = ({ item, progressRef }) => {
    const meshRef = useRef<THREE.Mesh>(null);

    useFrame(() => {
        if (!meshRef.current) return;
        const p = easeInOut(clamp01(progressRef.current));
        meshRef.current.position.set(
            item.startPos[0] + (item.targetPos[0] - item.startPos[0]) * p,
            item.startPos[1] + (item.targetPos[1] - item.startPos[1]) * p,
            item.startPos[2] + (item.targetPos[2] - item.startPos[2]) * p,
        );
    });

    return (
        <Board
            ref={meshRef}
            name={item.key}
            position={item.startPos}
            rotation={item.targetRot}
            material={item.material}
            vertices={item.vertices}
            uvs={item.uvs}
            indices={item.indices}
        />
    );
};

type Phase = 'assembling' | 'holdIn' | 'disassembling' | 'holdOut' | 'done';

// ─── Постоянное вращение всей конструкции (как в слайдере) ────────────────────
const RotatingRig: React.FC<{
    items: FlatItem[];
    cfg: Required<AssemblyConfig>;
    runId: number;
    speed?: number;
}> = ({ items, cfg, runId, speed = 0.4 }) => {
    const rootRef = useRef<THREE.Group>(null);
    useFrame((_, delta) => {
        if (rootRef.current) rootRef.current.rotation.y += delta * speed;
    });

    // группируем элементы по родительской группе, чтобы сохранить groupPos/groupRot
    const groups = useMemo(() => {
        const byGi = new Map<number, FlatItem[]>();
        items.forEach((it) => {
            if (!byGi.has(it.groupIndex)) byGi.set(it.groupIndex, []);
            byGi.get(it.groupIndex)!.push(it);
        });
        return Array.from(byGi.entries()).map(([gi, its]) => ({
            gi,
            groupPos: its[0].groupPos,
            groupRot: its[0].groupRot,
            items: its,
        }));
    }, [items]);

    return (
        <group ref={rootRef} position={[0, -2, 0]}>
            <AssemblyDirector groups={groups} cfg={cfg} runId={runId} totalCount={items.length} />
        </group>
    );
};

// Дирижёр: влёт per-element, родитель хранит groupPos/Rot
const AssemblyDirector: React.FC<{
    groups: { gi: number; groupPos: [number, number, number]; groupRot: [number, number, number]; items: FlatItem[] }[];
    cfg: Required<AssemblyConfig>;
    runId: number;
    totalCount: number;
}> = ({ groups, cfg, runId, totalCount }) => {
    const progressRefs = useRef<Map<string, React.MutableRefObject<number>>>(new Map());
    groups.forEach((g) =>
        g.items.forEach((it) => {
            if (!progressRefs.current.has(it.key)) progressRefs.current.set(it.key, { current: 0 });
        })
    );

    // порядок сборки: сперва группы из groupOrder, затем остальные (по возрастанию gi)
    const order = useMemo(() => {
        const present = groups.map((g) => g.gi);
        const wanted = (cfg.groupOrder ?? []).filter((gi) => present.includes(gi));
        const rest = present.filter((gi) => !wanted.includes(gi)).sort((a, b) => a - b);
        const seq = [...wanted, ...rest];

        const byGi = new Map<number, FlatItem[]>();
        groups.forEach((g) => byGi.set(g.gi, g.items));

        const flat: string[] = [];
        seq.forEach((gi) => (byGi.get(gi) ?? []).forEach((it) => flat.push(it.key)));
        return flat;
    }, [groups, cfg.groupOrder]);

    const clock = useRef(0);
    const phase = useRef<Phase>('assembling');
    const n = totalCount;
    const assembleTotal = (n - 1) * cfg.stagger + cfg.itemDuration;

    useEffect(() => {
        clock.current = 0;
        phase.current = cfg.autoStart ? 'assembling' : 'done';
        progressRefs.current.forEach((r) => (r.current = cfg.autoStart ? 0 : 1));
    }, [runId, cfg.autoStart]);

    useFrame((_, delta) => {
        clock.current += delta * 1000;
        const t = clock.current;
        const set = (key: string, v: number) => {
            const r = progressRefs.current.get(key);
            if (r) r.current = v;
        };

        switch (phase.current) {
            case 'assembling': {
                order.forEach((key, i) => set(key, clamp01((t - i * cfg.stagger) / cfg.itemDuration)));
                if (t >= assembleTotal) {
                    order.forEach((key) => set(key, 1));
                    phase.current = 'holdIn';
                    clock.current = 0;
                }
                break;
            }
            case 'holdIn':
                if (t >= cfg.holdMs) {
                    phase.current = 'disassembling';
                    clock.current = 0;
                }
                break;
            case 'disassembling': {
                order.forEach((key, i) =>
                    set(key, 1 - clamp01((t - (n - 1 - i) * cfg.stagger) / cfg.itemDuration))
                );
                if (t >= assembleTotal) {
                    order.forEach((key) => set(key, 0));
                    phase.current = cfg.loop ? 'holdOut' : 'done';
                    clock.current = 0;
                }
                break;
            }
            case 'holdOut':
                if (t >= cfg.holdMs) {
                    phase.current = 'assembling';
                    clock.current = 0;
                }
                break;
            default:
                break;
        }
    });

    return (
        <>
            {groups.map((g) => (
                <group
                    key={g.gi}
                    position={g.groupPos}
                    rotation={new THREE.Euler(...g.groupRot)}
                >
                    {g.items.map((it) => (
                        <FlyBoard key={it.key} item={it} progressRef={progressRefs.current.get(it.key)!} />
                    ))}
                </group>
            ))}
        </>
    );
};

// ─── App ─────────────────────────────────────────────────────────────────────
export default function App(props: AssemblyConfig & { modelUrl?: string; cameraPos?: [number, number, number] }) {
    const cfg: Required<AssemblyConfig> = { ...DEFAULTS, ...props };
    const modelUrl = props.modelUrl ?? MODEL_URL;
    const cameraPos = props.cameraPos ?? [15, 2, 2];

    const [items, setItems] = useState<FlatItem[]>([]);
    const [runId, setRunId] = useState(0);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetch(modelUrl)
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => {
                if (cancelled) return;
                const struct = fromRenderStructDTOArray(data);
                const { items } = buildItems(struct as any[], cfg);
                setItems(items);
                setRunId((x) => x + 1);
            })
            .catch((e) => !cancelled && setError(String(e)));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modelUrl, cfg.hideDistance, cfg.liftFactor, cfg.spreadFactor, cfg.spreadBase]);

    return (
        <div style={{ width: '100%', height: '100%', background: '#ffffff', position: 'relative' }}>
            {error && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                    {error}
                </div>
            )}
            <Suspense fallback={null}>
                <Canvas camera={{ position: cameraPos, fov: 30 }} gl={{ antialias: true }}>
                    <LightV1 />
                    {items.length > 0 && (
                        <RotatingRig items={items} cfg={cfg} runId={runId} />
                    )}
                    <SceneEnvironment sky={false} floor={false} />
                </Canvas>
            </Suspense>

            {!cfg.loop && (
                <button
                    onClick={() => setRunId((x) => x + 1)}
                    style={{
                        position: 'absolute',
                        bottom: 12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        padding: '6px 14px',
                        borderRadius: 999,
                        border: '1px solid rgba(0,0,0,0.1)',
                        background: 'rgba(0,0,0,0.55)',
                        color: '#fff',
                        fontSize: 12,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        cursor: 'pointer',
                        backdropFilter: 'blur(4px)',
                    }}
                >
                    Replay
                </button>
            )}
        </div>
    );
}
